import { useMemo } from 'react';
import { useOrganizationId } from '@features/user';

import { requirementKindLabel } from '../constants';
import { resolveDisplayState } from '../vocabulary';

import { useGetPatientRequirementsQuery } from './api';
import { useRequirementCatalog } from './hooks';

/**
 * How often the recording window re-reads while something is outstanding.
 *
 * AC-15 allows a poll or a refetch on navigation. Polling only while items are
 * open means a provider whose patient completes the card mid-visit sees the
 * alert clear on its own, and a patient with nothing outstanding costs nothing.
 */
const OPEN_REQUIREMENTS_POLL_MS = 60_000;

export interface OpenRequirementItem {
  patientRequirementId: string;
  /**
   * What to call the item. Falls back through the practice's label, then the
   * kind's staff-facing name, then undefined — the alert renders a count rather
   * than a UUID when nothing can name it.
   */
  label?: string;
}

export interface OpenRequirementsResult {
  openCount: number;
  items: OpenRequirementItem[];
  /** True when at least one item could not be named. Drives count-only copy. */
  hasUnnamedItems: boolean;
  isLoading: boolean;
}

/**
 * A patient's outstanding requirements, for the provider alert (GDN-3225, AC-15).
 *
 * The per-patient row carries only a definition id — no kind, no label — so
 * naming an item means joining the org catalog. That join is best-effort: if
 * the catalog is unavailable (a provider may not be able to read a
 * practice-admin surface), the alert still reports how many items are open
 * rather than showing identifiers to someone mid-visit.
 */
export const useOpenRequirements = (
  patientId: string | undefined,
  /**
   * False when the caller will not render. Hooks cannot be called
   * conditionally, so without this the recording window would read the org
   * catalog and the patient's requirements on every encounter in an org whose
   * gate is off — for a badge nobody sees.
   */
  enabled: boolean = true,
): OpenRequirementsResult => {
  const organizationId = useOrganizationId();
  const skip = !enabled || !organizationId || !patientId;

  const { data, isLoading } = useGetPatientRequirementsQuery(
    { orgId: organizationId ?? '', patientId: patientId ?? '' },
    {
      skip,
      // Clears the alert on the next visit to this patient without a reload.
      refetchOnMountOrArgChange: true,
    },
  );

  const openItems = useMemo(
    () =>
      (data?.items ?? []).filter(
        item => resolveDisplayState(item, false) === 'missing',
      ),
    [data],
  );

  // Keep re-reading only while something is outstanding.
  useGetPatientRequirementsQuery(
    { orgId: organizationId ?? '', patientId: patientId ?? '' },
    {
      skip: skip || openItems.length === 0,
      pollingInterval: OPEN_REQUIREMENTS_POLL_MS,
    },
  );

  // Naming items is decoration; never fetch the catalog for a hidden badge.
  const catalog = useRequirementCatalog({ skip });

  const items = useMemo<OpenRequirementItem[]>(() => {
    const byId = new Map(
      catalog.definitions.map(definition => [
        definition.patient_requirement_definition_id,
        definition,
      ]),
    );
    return openItems.map(item => {
      const definition = byId.get(item.patient_requirement_definition_id);
      return {
        patientRequirementId: item.patient_requirement_id,
        label: definition
          ? definition.label || requirementKindLabel(definition.kind)
          : undefined,
      };
    });
  }, [openItems, catalog.definitions]);

  return {
    openCount: items.length,
    items,
    hasUnnamedItems: items.some(item => item.label === undefined),
    // The catalog is decoration here; never hold the alert back for it.
    isLoading: !skip && isLoading,
  };
};
